import React, { useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBookings } from '../hooks/useBookings';
import Topbar from '../components/layout/Topbar';
import { BookingList } from '../components/bookings/BookingItem';
import { Button, Panel, PanelBody, Spinner } from '../components/ui';
import { createOrderManual } from '../services/firebase';

const STATUS_FILTERS = ['Tutte', 'Confermate', 'In attesa', 'Cancellate'];
const SERVICE_FILTERS = ['Tutti', 'Ombrelloni', 'Lettini', 'Cabine'];

export default function Bookings() {
  const { admin } = useAuth();
  const { orders, loading, cancel, confirm, refetch } = useBookings(admin?.beachCode);

  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState('Tutte');
  const [serviceFilter, setServiceFilter] = useState('Tutti');
  const [showNewForm, setShowNewForm]  = useState(false);

  const filtered = useMemo(() => {
    return orders.filter((o) => {
      const matchSearch = !search ||
        (o.guestName || o.userId || '').toLowerCase().includes(search.toLowerCase()) ||
        o.id.toLowerCase().includes(search.toLowerCase());

      const matchStatus =
        statusFilter === 'Tutte' ||
        (statusFilter === 'Confermate' && o.status === 'confirmed') ||
        (statusFilter === 'In attesa' && o.status === 'pending') ||
        (statusFilter === 'Cancellate' && o.status === 'cancelled');

      const matchService =
        serviceFilter === 'Tutti' ||
        o.serviceSelected?.toLowerCase().includes(serviceFilter.toLowerCase());

      return matchSearch && matchStatus && matchService;
    });
  }, [orders, search, statusFilter, serviceFilter]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Topbar
        title="Prenotazioni"
        actions={
          <Button variant="primary" onClick={() => setShowNewForm(!showNewForm)}>
            + Nuova prenotazione
          </Button>
        }
      />

      <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
        {showNewForm && (
          <NewBookingForm
            beachCode={admin?.beachCode}
            onSave={async (data) => { await createOrderManual(data); setShowNewForm(false); refetch(); }}
            onCancel={() => setShowNewForm(false)}
          />
        )}

        <Panel>
          <PanelBody>
            {/* Search */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)', padding: '0 12px', marginBottom: '14px',
            }}>
              <SearchIcon />
              <input
                type="text"
                placeholder="Cerca per nome, ID..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  background: 'transparent', border: 'none', color: '#fff',
                  fontSize: '13px', padding: '9px 0', flex: 1, outline: 'none',
                  fontFamily: 'var(--font-sans)',
                }}
              />
            </div>

            {/* Status chips */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px' }}>
              {STATUS_FILTERS.map((f) => (
                <Chip key={f} label={f} active={statusFilter === f} onClick={() => setStatusFilter(f)} />
              ))}
              <div style={{ width: '1px', background: 'var(--border)', margin: '0 4px' }} />
              {SERVICE_FILTERS.map((f) => (
                <Chip key={f} label={f} active={serviceFilter === f} onClick={() => setServiceFilter(f)} />
              ))}
            </div>

            {/* Count */}
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
              {filtered.length} prenotazion{filtered.length === 1 ? 'e' : 'i'}
            </div>

            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Spinner size={28} />
              </div>
            ) : (
              <BookingList orders={filtered} onCancel={cancel} onConfirm={confirm} />
            )}
          </PanelBody>
        </Panel>
      </div>
    </div>
  );
}

function Chip({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 12px', borderRadius: '20px', fontSize: '12px',
        border: '1px solid var(--border)',
        background: active ? 'var(--ocean)' : 'var(--surface)',
        borderColor: active ? 'var(--ocean)' : 'var(--border)',
        color: active ? '#fff' : 'var(--text-soft)',
        cursor: 'pointer', fontFamily: 'var(--font-sans)', transition: 'all 0.15s',
      }}
    >
      {label}
    </button>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="6" cy="6" r="4.5" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2" />
      <line x1="9.5" y1="9.5" x2="13" y2="13" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function NewBookingForm({ beachCode, onSave, onCancel }) {
  const [form, setForm] = useState({
    serviceSelected: 'ombrelloni', quantity: 1,
    totalPrice: 0, notes: '',
    date: new Date().toISOString().split('T')[0],
  });
  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

  const handleSave = () => onSave({
    beachCode,
    serviceSelected: form.serviceSelected,
    quantity: Number(form.quantity),
    totalPrice: Number(form.totalPrice),
    selectedDate: [form.date],
    notes: form.notes,
  });

  const inputStyle = {
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)', color: '#fff',
    padding: '8px 12px', fontSize: '13px', fontFamily: 'var(--font-sans)',
    width: '100%',
  };

  return (
    <div style={{
      background: 'var(--navy-mid)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '20px', marginBottom: '16px',
    }}>
      <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '16px' }}>
        Nuova prenotazione manuale
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '12px' }}>
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Servizio</label>
          <select value={form.serviceSelected} onChange={set('serviceSelected')} style={inputStyle}>
            <option value="ombrelloni">Ombrelloni</option>
            <option value="lettini">Lettini</option>
            <option value="cabine">Cabine</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Quantità</label>
          <input type="number" min="1" value={form.quantity} onChange={set('quantity')} style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Totale €</label>
          <input type="number" min="0" value={form.totalPrice} onChange={set('totalPrice')} style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Data</label>
          <input type="date" value={form.date} onChange={set('date')} style={inputStyle} />
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Note</label>
          <input type="text" placeholder="Opzionale..." value={form.notes} onChange={set('notes')} style={inputStyle} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button variant="primary" onClick={handleSave}>Salva prenotazione</Button>
        <Button variant="ghost" onClick={onCancel}>Annulla</Button>
      </div>
    </div>
  );
}
